import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import { LoadingSpinner, PageError } from '@/components/ui';
import { CreateProductForm } from './components/CreateProductForm';
import { ProductList } from './components/ProductList';
import { ProductDetail } from './components/ProductDetail';
import { Product } from '@haccp/api-client';

const BoxIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 015.646 5.646 9 9 0 0120.354 15.354z" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

export default function TraceabilityPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchSupplier, setSearchSupplier] = useState('');
  const [printingProductId, setPrintingProductId] = useState<string | null>(null);
  const token = useAuthStore((state) => state.token);

  const { data: productsData, isLoading: isLoadingProducts, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: () => apiClient.getProducts(0, 50),
  });

  const deleteProductMutation = useMutation({
    mutationFn: (productId: string) => apiClient.deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProduct(null);
      addToast(t('traceability.deleteSuccess'), 'success');
    },
    onError: () => {
      addToast(t('traceability.deleteError'), 'error');
    },
  });

  const handlePrintLabel = async (productId: string) => {
    try {
      setPrintingProductId(productId);
      const response = await fetch(`/api/traceability/products/${productId}/label`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch label');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `label-product-${productId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      addToast(t('traceability.printError'), 'error');
    } finally {
      setPrintingProductId(null);
    }
  };

  const products: Product[] = productsData?.data ?? [];
  const isOwner = user?.role === 'OWNER';

  const filteredProducts = searchSupplier
    ? products.filter((p: Product) =>
        p.supplier.toLowerCase().includes(searchSupplier.toLowerCase())
      )
    : products;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BoxIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('traceability.title')}</h1>
              <p className="text-slate-600">{t('traceability.subtitle', { defaultValue: '' })}</p>
            </div>
          </div>

          {(user?.role === 'OWNER' || user?.role === 'STAFF') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <PlusIcon className="w-5 h-5" />
              {t('traceability.addProduct')}
            </button>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder={t('traceability.searchPlaceholder')}
            value={searchSupplier}
            onChange={(e) => setSearchSupplier(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {isLoadingProducts ? (
        <LoadingSpinner message={t('traceability.loadingProducts')} />
      ) : productsError ? (
        <PageError message={t('traceability.loadError')} />
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-dashed border-slate-300">
          <BoxIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600 text-lg">
            {searchSupplier ? t('traceability.noSearchResult') : t('traceability.empty')}
          </p>
          {!searchSupplier && (user?.role === 'OWNER' || user?.role === 'STAFF') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
            >
              <PlusIcon className="w-5 h-5" />
              {t('traceability.createFirst')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product: Product) => (
            <ProductList
              key={product.id}
              product={product}
              onViewDetail={() => setSelectedProduct(product)}
              onPrintLabel={() => handlePrintLabel(product.id)}
              isPrinting={printingProductId === product.id}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateProductForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}

      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onDelete={(productId) => {
            deleteProductMutation.mutate(productId);
          }}
          isDeleting={deleteProductMutation.isPending}
          canDelete={isOwner}
        />
      )}
    </div>
  );
}
